import React, { useState } from "react";
import { Lock, Mail, LogIn, AlertCircle, Eye, EyeOff } from "lucide-react";
import { useAdminAuth } from "@/lib/adminAuth";
import { RESTAURANT } from "@/data/catalog";

export const AdminLogin = () => {
  const { login } = useAdminAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : "Connexion impossible. Vérifiez vos identifiants."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-4"
      data-testid="admin-login-screen"
    >
      {/* Halo orange */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 mx-auto h-[420px] max-w-2xl"
        style={{
          background:
            "radial-gradient(60% 80% at 50% 18%, rgba(255,149,0,0.18) 0%, rgba(255,149,0,0.05) 38%, transparent 70%)",
        }}
      />

      <form
        onSubmit={handleSubmit}
        className="relative w-full max-w-md rounded-2xl border border-[#262626] bg-[#0F0F0F] p-7 sm:p-8 shadow-[0_20px_50px_rgba(0,0,0,0.5)]"
        data-testid="admin-login-form"
      >
        <div className="text-center mb-6">
          <div className="font-display text-4xl text-white leading-none">
            LE <span className="text-[#FF9500]">11</span>
          </div>
          <div className="mt-2 text-[10px] uppercase tracking-[0.32em] text-[#FF9500] font-street">
            Espace restaurateur
          </div>
          <h1 className="font-street mt-3 text-2xl tracking-wide">
            Connexion admin
          </h1>
        </div>

        {/* Email */}
        <label className="block mb-3">
          <span className="text-[10px] uppercase tracking-[0.22em] text-white/45 font-street">
            Email
          </span>
          <div className="mt-1 relative">
            <Mail
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"
            />
            <input
              type="email"
              required
              autoComplete="email"
              data-testid="admin-login-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="contact@pizza11.fr"
              className="w-full rounded-md border border-[#262626] bg-[#0A0A0A] py-2.5 pl-9 pr-3 text-sm text-white placeholder:text-white/30 focus:border-[#FF9500] focus:outline-none transition-colors"
            />
          </div>
        </label>

        {/* Password */}
        <label className="block mb-4">
          <span className="text-[10px] uppercase tracking-[0.22em] text-white/45 font-street">
            Mot de passe
          </span>
          <div className="mt-1 relative">
            <Lock
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"
            />
            <input
              type={showPwd ? "text" : "password"}
              required
              autoComplete="current-password"
              data-testid="admin-login-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-md border border-[#262626] bg-[#0A0A0A] py-2.5 pl-9 pr-10 text-sm text-white focus:border-[#FF9500] focus:outline-none transition-colors"
            />
            <button
              type="button"
              onClick={() => setShowPwd((v) => !v)}
              data-testid="admin-login-toggle-pwd"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
              aria-label={showPwd ? "Masquer" : "Afficher"}
            >
              {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>
        </label>

        {error && (
          <div
            data-testid="admin-login-error"
            role="alert"
            className="mb-4 inline-flex items-start gap-2 rounded-md border border-[#DC2626]/40 bg-[#DC2626]/10 px-3 py-2 text-[12px] text-[#FCA5A5] leading-snug w-full"
          >
            <AlertCircle size={13} className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="w-full font-street inline-flex items-center justify-center gap-2 rounded-md bg-[#FF9500] px-5 py-3 text-base tracking-wider text-black shadow-[0_4px_0_#7a4900] hover:-translate-y-0.5 hover:shadow-[0_6px_0_#7a4900] disabled:opacity-60 disabled:cursor-not-allowed transition-all"
        >
          <LogIn size={17} strokeWidth={2.6} />
          {loading ? "Connexion…" : "Se connecter"}
        </button>

        <div className="mt-5 text-center text-[11px] text-white/40">
          {RESTAURANT.name} · {RESTAURANT.domain}
        </div>
      </form>
    </div>
  );
};

export default AdminLogin;
